import React, { useState, useCallback } from "react";
import { ShieldCheck, QrCode, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const TwoFactorSection = () => {
  const [status, setStatus] = useState(null); // null=loading, true=enabled, false=disabled
  const [setupData, setSetupData] = useState(null); // {secret, uri}
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const checkStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/2fa/status`);
      setStatus(res.data.enabled);
    } catch { setStatus(false); }
  }, []);

  React.useEffect(() => { checkStatus(); }, [checkStatus]);

  const startSetup = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/2fa/setup`);
      setSetupData(res.data);
      setMessage("");
    } catch (e) {
      setMessage(e.response?.data?.detail || "Erreur");
    }
    setLoading(false);
  };

  const enable2FA = async () => {
    if (!code || code.length !== 6) return;
    setLoading(true);
    try {
      await axios.post(`${API}/admin/2fa/enable`, { code });
      setMessage("2FA activé !");
      setSetupData(null);
      setCode("");
      setStatus(true);
    } catch (e) {
      setMessage(e.response?.data?.detail || "Code invalide");
    }
    setLoading(false);
  };

  const disable2FA = async () => {
    if (!code || code.length !== 6) return;
    setLoading(true);
    try {
      await axios.post(`${API}/admin/2fa/disable`, { code });
      setMessage("2FA désactivé");
      setCode("");
      setStatus(false);
    } catch (e) {
      setMessage(e.response?.data?.detail || "Code invalide");
    }
    setLoading(false);
  };

  return (
    <div className="bg-card rounded-lg border p-6" data-testid="2fa-section">
      <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
        <ShieldCheck className="w-5 h-5 text-primary" /> Authentification 2FA
        {status === true && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded font-medium">Activé</span>}
        {status === false && <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded font-medium">Désactivé</span>}
      </h2>

      {/* Setup flow */}
      {!status && !setupData && (
        <div>
          <p className="text-sm text-muted-foreground mb-4">
            Ajoutez une couche de sécurité supplémentaire. Vous aurez besoin d'une app comme Google Authenticator ou Authy.
          </p>
          <Button onClick={startSetup} disabled={loading}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <QrCode className="w-4 h-4 mr-2" />}
            Configurer le 2FA
          </Button>
        </div>
      )}

      {/* QR Code display */}
      {setupData && (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">Scannez ce QR code avec votre app d'authentification :</p>
          <div className="bg-white border-2 border-slate-200 rounded-lg p-4 text-center">
            <img
              src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(setupData.uri)}`}
              alt="QR Code 2FA"
              className="mx-auto w-48 h-48"
            />
            <details className="mt-3">
              <summary className="text-xs text-muted-foreground cursor-pointer">Clé manuelle</summary>
              <code className="text-xs bg-muted px-2 py-1 rounded mt-1 block break-all">{setupData.secret}</code>
            </details>
          </div>
          <div>
            <label className="text-sm font-semibold">Entrez le code 6 chiffres pour confirmer :</label>
            <div className="flex gap-2 mt-2">
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="123456"
                maxLength={6}
                className="w-32 text-center font-mono text-lg tracking-widest"
              />
              <Button onClick={enable2FA} disabled={loading || code.length !== 6}>
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Activer"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Disable flow */}
      {status === true && !setupData && (
        <div>
          <p className="text-sm text-muted-foreground mb-3">Le 2FA est actif. Entrez un code pour le désactiver :</p>
          <div className="flex gap-2">
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="123456"
              maxLength={6}
              className="w-32 text-center font-mono text-lg tracking-widest"
            />
            <Button variant="destructive" onClick={disable2FA} disabled={loading || code.length !== 6}>
              Désactiver 2FA
            </Button>
          </div>
        </div>
      )}

      {message && <p className="text-sm mt-3 font-medium text-accent">{message}</p>}
    </div>
  );
};
